import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwZHVzayUyMGZyZWlnaHR8ZW58MHx8fHwxNzg4ODk4NzQyfDA&ixlib=rb-4.1.0&q=85",
    badge: "DOT / FMCSA Compliance Partner",
    title: null,
    subtitle: null,
    cta: { label: "Start Your Trucking Company", to: "/contact" },
  },
  {
    image: "https://images.unsplash.com/photo-1655478157959-3fcfc543c1a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwZHVzayUyMGZyZWlnaHR8ZW58MHx8fHwxNzg4ODk4NzQyfDA&ixlib=rb-4.1.0&q=85",
    badge: "Permit Deadline Watchdogs",
    title: "IFTA, IRP & 2290 — Filed Before the Deadline",
    subtitle: "We track every expiry date across all 50 states and file early, so your trucks never sit.",
    cta: { label: "See Must-Know Dates", to: "#deadlines" },
  },
  {
    image: "https://images.unsplash.com/photo-1504846257989-a76209d9d2ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwZHVzayUyMGZyZWlnaHR8ZW58MHx8fHwxNzg4ODk4NzQyfDA&ixlib=rb-4.1.0&q=85",
    badge: "New Carriers Welcome",
    title: "Your Motor Carrier Authority — Granted in 3 Weeks",
    subtitle: "LLC, EIN, DOT number, MC authority, BOC-3 and insurance. One team, one flat quote.",
    cta: { label: "Get My Authority", to: "/services/mc-motor-carrier-authority" },
  },
];

export default function HeroSlider({ content }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [paused, go]);

  const slide = SLIDES[index];
  const title = slide.title || content?.hero_title || "Expert Truck Permit Consulting & Licensing";
  const subtitle = slide.subtitle || content?.hero_subtitle || "Registrations, fuel taxes, permits and safety — handled hassle-free.";
  const phone = content?.phone || "(380) 800-6365";
  const stats = content?.stats || [];

  const handleCta = (e, to) => {
    if (to.startsWith("#")) {
      e.preventDefault();
      document.getElementById(to.slice(1))?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden grain-overlay"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}>
      <AnimatePresence mode="sync">
        <motion.div key={index} className="absolute inset-0"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 1 }}>
          <motion.img src={slide.image} alt="" className="w-full h-full object-cover"
            initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6.5, ease: "linear" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/90 to-[#0B132B]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <div className="max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div key={index}
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}>
              <div className="inline-flex items-center gap-2 border border-[#F97316]/40 bg-[#F97316]/10 rounded-full px-4 py-1.5 mb-7">
                <span className="w-2 h-2 rounded-full bg-[#F97316] animate-glow" />
                <span className="font-mono2 text-[11px] uppercase tracking-widest text-[#F97316]" data-testid="hero-badge">{slide.badge}</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[0.95]" data-testid="hero-title">
                {title}
              </h1>
              <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                {subtitle}
              </p>
              <div className="flex flex-wrap gap-4 mt-9">
                <Link to={slide.cta.to} onClick={(e) => handleCta(e, slide.cta.to)} data-testid="hero-consultation-btn"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-sm px-7 py-3.5 rounded-md transition-all duration-200 hover:translate-y-[-2px] flex items-center gap-2">
                  {slide.cta.label} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="hero-call-btn"
                  className="border border-slate-600 hover:border-[#F97316] text-white font-semibold text-sm px-7 py-3.5 rounded-md transition-all duration-200 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#F97316]" /> {phone}
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-4 mt-12" data-testid="hero-slider-controls">
          <button onClick={() => go(-1)} data-testid="hero-prev-btn" aria-label="Previous slide"
            className="w-10 h-10 rounded-full border border-slate-600 flex items-center justify-center text-slate-300 hover:border-[#F97316] hover:text-[#F97316] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => go(1)} data-testid="hero-next-btn" aria-label="Next slide"
            className="w-10 h-10 rounded-full border border-slate-600 flex items-center justify-center text-slate-300 hover:border-[#F97316] hover:text-[#F97316] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="flex gap-2 ml-2">
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-10 bg-[#F97316]" : "w-4 bg-slate-600 hover:bg-slate-500"}`} />
            ))}
          </div>
        </div>

        {stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E293B] border border-[#1E293B] rounded-lg overflow-hidden mt-12" data-testid="hero-stats">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#0f1830]/90 backdrop-blur px-6 py-5">
                <div className="font-mono2 text-2xl sm:text-3xl font-bold text-[#F97316]">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
